import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hpath } from '../lib/api.js';
import { resetSessionCache } from './queryClient.js';
import { setFormatSettings } from '../lib/format.js';
import { todayIn } from '../lib/dates.js';

const SessionContext = createContext(null);

async function fetchMe() {
  try {
    return await api('/api/auth/me');
  } catch (err) {
    if (err.status === 401) return null;
    throw err;
  }
}

/** Sessão do usuário + lar ativo. O lar ativo é validado pelo servidor (participação real). */
export function SessionProvider({ children }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000, refetchInterval: false });
  const data = me.data;
  const householdId = data?.activeHouseholdId ?? null;

  const householdQuery = useQuery({
    queryKey: [householdId, 'household'],
    queryFn: () => api(hpath(householdId)),
    enabled: Boolean(householdId),
    staleTime: 60_000,
  });

  const household =
    householdQuery.data?.household ?? data?.households?.find((h) => h.id === householdId) ?? null;

  useEffect(() => {
    setFormatSettings({
      locale: data?.user?.preferences?.locale ?? 'pt-BR',
      currency: household?.currency ?? 'BRL',
      timeZone: household?.timezone ?? 'America/Sao_Paulo',
    });
  }, [data?.user?.preferences?.locale, household?.currency, household?.timezone]);

  const setMe = useCallback((next) => qc.setQueryData(['me'], next), [qc]);

  const switchHousehold = useCallback(
    async (id) => {
      await api('/api/account/active-household', { method: 'PUT', body: { householdId: id } });
      const cur = qc.getQueryData(['me']);
      qc.setQueryData(['me'], { ...cur, activeHouseholdId: id });
      await qc.invalidateQueries({ queryKey: ['me'] });
    },
    [qc],
  );

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    resetSessionCache(qc);
  }, [qc]);

  const value = useMemo(
    () => ({
      loading: me.isPending,
      error: me.isError ? me.error : null,
      retry: me.refetch,
      user: data?.user ?? null,
      households: data?.households ?? [],
      // Papéis da plataforma (super_admin, suporte). Vazio para quem só usa a Gestão do Lar.
      platformRoles: data?.platformRoles ?? [],
      // Condomínios onde a pessoa é da equipe ou tem vínculo (pendente ou aprovado).
      condominiums: data?.condominiums ?? [],
      // Situação do cadastro de prestador do Larelo (null = não é prestador).
      professionalStatus: data?.professionalStatus ?? null,
      // Suporte vendo o sistema como esta pessoa (só leitura). null fora da impersonação.
      impersonation: data?.impersonation ?? null,
      householdId,
      household,
      role: householdQuery.data?.role ?? data?.households?.find((h) => h.id === householdId)?.role ?? null,
      permissions: householdQuery.data?.permissions ?? {},
      members: householdQuery.data?.members ?? [],
      today: todayIn(household?.timezone ?? 'America/Sao_Paulo'),
      setMe,
      switchHousehold,
      logout,
      refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
    }),
    [
      me.isPending,
      me.isError,
      me.error,
      me.refetch,
      data,
      householdId,
      household,
      householdQuery.data,
      setMe,
      switchHousehold,
      logout,
      qc,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}

/** Atalho: caminho de API do lar ativo. */
export function useHpath() {
  const { householdId } = useSession();
  return useCallback((path = '') => hpath(householdId, path), [householdId]);
}
